// @flow

// !!! IMPORTANT !!!
//
// This file affects the global flows redux state, used in lots of places.
// You may need to make the same changes to both the beta-editor and
// classic-editor code. Edit with caution and test changes thoroughly!
import _ from 'lodash';
import { $apply, $each, $merge, $set, update } from 'qim';

import FieldUtils from 'app/beta-editor/utils/FieldUtils';
import RuleUtils from 'app/beta-editor/utils/RuleUtils';
import ServiceUtils from 'app/service/ServiceUtils';
import lockPromise from 'app/common/lockPromise';
import z from 'app/common/z';
import Errors, { resourceNames } from 'app/errors';
import fetch, {
  fetchJson,
  getJsonWithAccountId,
  postJson,
  postJsonWithAccountId,
} from 'app/common/fetch';
import { PATHS_GROUP_ATTRS } from 'app/beta-editor/utils/PathUtils';
import { getNextTaskState } from 'app/beta-editor/utils/FlowTypedTaskStateUtils';
import { replaceParams } from 'app/common/CommonUtils';
import { sortNodes } from 'app/beta-editor/utils/NodeTrees';
import { TRASH_DAYS, TRASH_FOLDER_ID } from 'app/dashboard/utils/constants';
import { ZT_API_NAMESPACE, ZT_API_PARAM } from 'app/beta-editor/constants';
import {
  addParamsToUrl,
  addTruthyParamsToUrl,
} from 'app/common/utils/UrlUtils';
import {
  convertDeprecatedParams,
  nodeTemplates,
  validateParams,
} from 'app/shared-zap/SharedZapUtils';
import {
  getNodesUrl,
  getZapNodesUrl,
  getZapUrl,
} from 'app/beta-editor/utils/FlowUtils';

import type {
  Flow,
  FlowNode,
  FlowNodeType,
  FlowQuery,
  FlowTaskState,
  Need,
} from 'app/beta-editor/flowState';
import type { FolderIdType } from 'app/dashboard/types';
import type { ID } from 'app/common/types';
import type { UnpauseOriginType } from 'app/beta-editor/types';

type IsTemplate = { isTemplate: boolean };
type CreateNodeParams = {
  before_id?: ID,
  parent_id: ID,
  root_id: ID,
  authentication_id?: ID,
  params?: {},
  title?: string,
  meta?: {},
  type_of: FlowNodeType,
  selected_api?: string,
  action?: string,
  guided_recipe_origin_id?: ID,
  isTemplate?: boolean,
};

const arrayToObject = (objects?: any) => {
  if (!objects) {
    return {};
  }

  return _.fromPairs(
    objects.map(item => {
      return [item.id, item];
    })
  );
};

const objectifyNodes = (flow: ?Flow): Flow => {
  return update(['nodes', $apply(arrayToObject)], flow);
};

const getZaps = (accountId, path, params = {}) => {
  return getJsonWithAccountId(
    accountId,
    addParamsToUrl(`${z.cfg.api}/${path}`, params)
  )
    .then(zaps =>
      _.fromPairs(
        zaps.objects.map(zap => {
          const nodes = arrayToObject(
            zap.nodes.map(node =>
              _.omit(
                {
                  ...node,
                  authentication_id: node.auth_id,
                },
                ['auth_id']
              )
            )
          );
          return [
            zap.id,
            {
              ...zap,
              authentication_id: zap.auth_id,
              nodes,
              nodeIds: sortNodes(zap.nodes).map(node => node.id),
            },
          ];
        })
      )
    )
    .catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.zaps,
        response,
      });
    });
};

export const isTemplateSuitableForCapabilities = (templates: Array<*>) => {
  if (!templates) {
    return false;
  }

  return (
    templates.length === 2 &&
    templates.every(
      template => template.selected_api && template.type_of && template.action
    )
  );
};

const EMPTY_RECENT_INCIDENTS = [];

const Data = {
  zaps(accountId: ID): Promise<Flow[]> {
    return Promise.all([
      Data.untrashedZaps(accountId),
      Data.trashedZaps(accountId),
    ]).then(([untrashed, trashed]) => ({
      ...untrashed,
      ...trashed,
    }));
  },

  untrashedZaps(accountId: ID) {
    return getZaps(accountId, 'zaps');
  },

  trashedZaps(accountId: ID) {
    return getZaps(accountId, 'zaps/trash', { trash_days: TRASH_DAYS }).then(
      update([$each, 'folder_ids', $set([TRASH_FOLDER_ID])])
    );
  },

  async newFlow(
    id: ID,
    nodesArray: [FlowNode],
    { isTemplate }: IsTemplate = {}
  ): Promise<{}> {
    try {
      const zap = await Data.zap(id, { isTemplate });

      const taskState = {
        state: {
          breadcrumbs: [],
          input: {},
          output: {},
          selected_sample: null,
          statuses: {},
        },
      };

      const nodes = arrayToObject(nodesArray);
      const nodeIds = sortNodes(nodes).map(node => node.id);
      return { ...zap, nodes, nodeIds, taskState };
    } catch (error) {
      throw new Error(error.message);
    }
  },

  async flow(id: ID, { isTemplate }: IsTemplate = {}): Promise<{}> {
    // First try to load the Zap, this ensures we bail out of loading the
    // nodes and task state if the Zap doesn't exist or we do not have access
    // to it.
    const zap = await Data.zap(id, { isTemplate });
    const [nodesArray, taskState] = await Promise.all([
      Data.nodes(id, { isTemplate }),
      Data.taskState(id, { isTemplate }),
    ]);

    const nodes = arrayToObject(nodesArray);
    const nodeIds = sortNodes(nodes).map(node => node.id);
    return { ...zap, nodes, nodeIds, taskState };
  },

  publicZap(guid: string) {
    return fetchJson(`/api/v3/zaps/shared/${guid}`).catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  node(nodeId: ID, { isTemplate }: IsTemplate = {}) {
    const params = isTemplate && ZT_API_PARAM;
    return fetchJson(
      addTruthyParamsToUrl(`${z.cfg.api}/nodes/${nodeId}`, params)
    ).catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.node,
        response,
      });
    });
  },

  flowPage(id: ID, nodeId?: ID, pageKey?: string): Promise<?{}> {
    if (!nodeId || !pageKey) {
      return Promise.resolve(null);
    }
    if (Data['flowPage_' + pageKey]) {
      return Data['flowPage_' + pageKey](id, nodeId);
    }
    return Promise.resolve({ nodeId, pageKey });
  },

  patchZap(
    id: ID,
    attrs: { title: string },
    { isTemplate }: IsTemplate = {}
  ): Promise<?Flow> {
    const url = addTruthyParamsToUrl(getZapUrl({ id, isTemplate }));
    return fetchJson(url, {
      method: 'PATCH',
      body: JSON.stringify(attrs),
    }).catch(response => {
      throw new Errors.DataPatchError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  zap(id: ID, { isTemplate }: IsTemplate = {}): Promise<$FlowFixMe> {
    return fetchJson(getZapUrl({ id, isTemplate })).catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  patchNode(
    id: ID,
    attrs: {},
    { isTemplate }: IsTemplate = {}
  ): Promise<?FlowNode> {
    return lockPromise(`nodes/${id}`, () => {
      return fetchJson(getNodesUrl({ id, isTemplate }), {
        method: 'PATCH',
        body: JSON.stringify(attrs),
      }).catch(response => {
        throw new Errors.DataPatchError({
          resource: resourceNames.node,
          response,
        });
      });
    });
  },

  nodes(id: ID, { isTemplate }: IsTemplate = {}): Promise<FlowNode[]> {
    return fetchJson(getZapNodesUrl({ id, isTemplate }))
      .then(result => (result ? result.objects : []))
      .catch(response => {
        throw new Errors.DataGetError({
          resource: resourceNames.nodes,
          response,
        });
      });
  },

  nodesNeeds(
    nodeIds: ID[],
    { isTemplate }: IsTemplate = {}
  ): Promise<{ [ID]: Need[] }> {
    const needsParams = (isTemplate && ZT_API_PARAM) || {};
    return Promise.all(
      nodeIds.map(nodeId =>
        Data.needs(nodeId, needsParams).then((needs): [ID, any] => [
          nodeId,
          needs,
        ])
      )
    )
      .then(_.fromPairs)
      .then(needs => {
        // Give every node needs even if it doesn't have any.
        needs = _.extend({}, needs);
        _.each(nodeIds, nodeId => {
          if (!needs[nodeId]) {
            needs[nodeId] = [];
          }
        });
        return needs;
      });
  },

  needs(nodeId: ID, params: {} = {}): Promise<$FlowFixMe> {
    // The backend returns a 409 if there's a problem getting custom fields
    // and there are no other fields. We'll treat 409 like a success.
    const statusWhiteList = [409];

    // 502 - 504 are considered non-catostrophic failures, handle that
    // as missing data rather than error.
    // 403 is the error code if we request needs without having configured a
    // selected_api/action/auth. We should be avoiding that request anyway, but
    // just in case one slips through, we won't throw an error.
    const statusErrorList = [502, 503, 504, 403];

    return fetch(fetch.url(`${z.cfg.api}/nodes/${nodeId}/needs`, params), {
      headers: new Headers({
        Accept: 'application/json',
      }),
    })
      .then(fetch.success)
      .catch(response => {
        if (
          _.includes([...statusWhiteList, ...statusErrorList], response.status)
        ) {
          return response; // Send a happy response if the status is whitelisted
        }

        throw response;
      })
      .then(response => {
        const isComplete = !_.includes(
          [206, ...statusWhiteList, ...statusErrorList],
          response.status
        );
        const isError = _.includes(statusErrorList, response.status);
        // 403 should never be visible to the user, so just ignore it
        const isIgnored = response.status === 403;
        return Promise.all([
          _.includes([...statusWhiteList, ...statusErrorList], response.status)
            ? []
            : response.json(),
          isComplete ? null : { isError, isIgnored },
        ]);
      })
      .then(([needs, missingInfo]) => {
        if (missingInfo) {
          const { isError, isIgnored } = missingInfo;
          if (isIgnored) {
            return [];
          }
          // $FlowFixMe
          needs = _.isArray(needs) ? needs : [];
          return needs.concat({
            isMissingFieldsPlaceholder: true,
            type: 'copy',
            isError,
          });
        }
        return needs;
      })
      .catch(response => {
        throw new Errors.DataGetError({
          resource: resourceNames.needs,
          response,
        });
      });
  },

  loadGives(nodeId: ID, { isTemplate }: IsTemplate = {}) {
    const base = `${z.cfg.api}/nodes/${nodeId}/states/gives/all`;
    const url = addTruthyParamsToUrl(base, {
      [ZT_API_NAMESPACE]: isTemplate,
    });
    return fetchJson(url).catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.givesAll,
        response,
      });
    });
  },

  givesAll(flow: Flow, nodeId: ID, { isTemplate }: IsTemplate = {}) {
    return Data.loadGives(nodeId, {
      isTemplate,
    });
  },

  invalidFieldMappings(
    nodeId: ID,
    nodeSample: { output: {} },
    { isTemplate }: IsTemplate = {}
  ) {
    return fetchJson(
      addTruthyParamsToUrl(`${z.cfg.api}/nodes/${nodeId}/child_field_errors`, {
        [ZT_API_NAMESPACE]: isTemplate,
      }),
      {
        method: 'POST',
        body: JSON.stringify(nodeSample.output || {}),
      }
    ).catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.fieldErrors,
        response,
      });
    });
  },

  taskState(id: ID, { isTemplate }: IsTemplate = {}) {
    // templates can use the app sample data in the event a node in the zap is not configured
    const fallback = isTemplate ? 'app' : 'disabled';

    const url = addTruthyParamsToUrl(
      `${z.cfg.api}/zaps/${id}/states?source=persisted&fallback=${fallback}`,
      { [ZT_API_NAMESPACE]: isTemplate }
    );
    return fetchJson(url)
      .then(json => (json ? json : {}))
      .catch(response => {
        throw new Errors.DataGetError({
          resource: resourceNames.taskStates,
          response,
        });
      });
  },

  createNode(
    accountId: ID,
    {
      before_id,
      parent_id,
      root_id,
      authentication_id,
      params = {},
      title,
      meta = {},
      type_of,
      selected_api,
      action,
      guided_recipe_origin_id,
      isTemplate,
    }: CreateNodeParams
  ) {
    const maybeTemplateQuery = (isTemplate && ZT_API_PARAM) || {};
    return postJsonWithAccountId(
      accountId,
      fetch.url(`${z.cfg.api}/nodes`, { before_id, ...maybeTemplateQuery }),
      {
        type_of,
        parent_id,
        root_id,
        selected_api,
        action,
        guided_recipe_origin_id,
        authentication_id,
        params,
        title,
        meta,
      }
    ).catch(response => {
      throw new Errors.DataPostError({
        resource: resourceNames.node,
        response,
      });
    });
  },

  autosetupNode(nodeId: ID) {
    return postJson(`${z.cfg.api}/nodes/${nodeId}/autosetup`).catch(
      response => {
        throw new Errors.DataPostError({
          resource: resourceNames.node,
          response,
        });
      }
    );
  },

  copyZap(zap: Flow) {
    return fetchJson(`${z.cfg.api}/nodes/${zap.id}/copy`, {
      method: 'POST',
      body: JSON.stringify(zap),
    }).then(objectifyNodes);
  },

  moveZap(zapId: ID, folderId: FolderIdType) {
    return fetchJson(`${z.cfg.api}/zaps/${zapId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        folder_id: folderId,
      }),
    })
      .then(objectifyNodes)
      .catch(response => {
        throw new Errors.DataPutError({
          resource: resourceNames.zap,
          response,
        });
      });
  },

  deleteZap(zapId: ID) {
    return fetchJson(`${z.cfg.api}/zaps/${zapId}`, {
      method: 'DELETE',
    }).catch(response => {
      throw new Errors.DataDeleteError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  deleteZaps(zapIds: ID[]) {
    return fetchJson(`${z.cfg.api}/zaps`, {
      method: 'DELETE',
      body: JSON.stringify({ zap_ids: zapIds }),
    }).catch(response => {
      throw new Errors.DataDeleteError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  deleteNode(
    nodeId: ID,
    {
      isTemplate,
      shouldCollapse,
    }: { isTemplate: boolean, shouldCollapse: boolean } = {}
  ) {
    const collapse = shouldCollapse ? 'yes' : '';
    const params = { collapse, [ZT_API_NAMESPACE]: isTemplate };

    return fetch(addTruthyParamsToUrl(`${z.cfg.api}/nodes/${nodeId}`, params), {
      method: 'DELETE',
    })
      .then(fetch.success)
      .catch(response => {
        throw new Errors.DataDeleteError({
          resource: resourceNames.node,
          response,
        });
      });
  },

  createSubscription(nodeId: ID, { isTemplate }: IsTemplate = {}) {
    const params = isTemplate && ZT_API_PARAM;
    return fetchJson(
      fetch.url(`${z.cfg.api}/nodes/${nodeId}/subscription`, params),
      {
        method: 'POST',
      }
    )
      .then(json => (json ? json.object : null))
      .catch(response => {
        throw new Errors.DataPostError({
          resource: resourceNames.nodeSubscription,
          response,
        });
      });
  },

  deleteSubscription(nodeId: ID, { isTemplate }: IsTemplate = {}) {
    const params = isTemplate && ZT_API_PARAM;
    return fetchJson(
      fetch.url(`${z.cfg.api}/nodes/${nodeId}/subscription`, params),
      {
        method: 'DELETE',
      }
    )
      .then(json => {
        // $FlowFixMe
        return json.object;
      })
      .catch(response => {
        throw new Errors.DataDeleteError({
          resource: resourceNames.nodeSubscription,
          response,
        });
      });
  },

  runNode(
    nodeId: ID,
    previousState: ?FlowTaskState,
    params: {} = {}
  ): Promise<{ state: FlowTaskState, hasCachedSample: boolean }> {
    return fetch(fetch.url(`${z.cfg.api}/nodes/${nodeId}/states`, params), {
      headers: new Headers({
        Accept: 'application/json',
      }),
    })
      .then(fetch.success)
      .then(response => {
        const hasCachedSample = response.status === 200; // Server will 206 on a cache miss
        return fetch.json(response).then(results => {
          // $FlowFixMe
          const runState: FlowTaskState = results.state;
          const state = getNextTaskState(previousState, runState);
          return {
            state,
            hasCachedSample,
          };
        });
      })
      .catch(response => {
        throw new Errors.DataPostError({
          resource: resourceNames.nodeSample,
          response,
        });
      });
  },

  deleteSample(nodeId: ID, { isTemplate }: IsTemplate = {}) {
    const params = {
      source: 'persisted',
      ...((isTemplate && ZT_API_PARAM) || {}),
    };
    return fetchJson(fetch.url(`${z.cfg.api}/nodes/${nodeId}/states`, params), {
      method: 'DELETE',
    }).catch(response => {
      throw new Errors.DataDeleteError({
        resource: resourceNames.nodeSample,
        response,
      });
    });
  },

  prefillChoices(
    nodeId: ID,
    taskState: FlowTaskState,
    field: any,
    params: {},
    page: number = 0,
    { isTemplate }: IsTemplate = {}
  ) {
    const prefill = field.prefill || field.parent.prefill;
    const pfdata = FieldUtils.splitPrefill(prefill);
    const itemName = ServiceUtils.actionNoun(field.meta.service, pfdata.action);
    const maybeTemplateQuery = (isTemplate && ZT_API_PARAM) || {};

    const query = {
      zap_meta_limit: -1,
      zap_meta_hydrate: false,
      zap_meta_page: page,
      zap_meta_prefill: true,
      ...maybeTemplateQuery,
    };

    taskState = update(['input', nodeId, $merge(params)], taskState);

    const url = fetch.url(
      `${z.cfg.api}/nodes/${nodeId}/samples/${pfdata.action}`,
      query
    );
    return fetchJson(url, {
      method: 'POST',
      body: JSON.stringify(taskState),
    })
      .catch(response => {
        throw new Errors.LoadItemsNetworkError({
          resource: resourceNames.dropdown,
          itemName,
          response,
        });
      })
      .then((data: $FlowFixMe) => {
        return FieldUtils.prefillChoices(
          data.objects,
          pfdata.displayKeys,
          pfdata.targetKey
        );
      })
      .catch(error => {
        if (error instanceof Errors.LoadItemsNetworkError) {
          throw error;
        }

        throw new Errors.LoadItemsError({
          resource: resourceNames.dropdown,
          itemName,
        });
      });
  },

  pause(flowId: ID) {
    return fetch(fetch.url(`${z.cfg.api}/nodes/${flowId}/pause`), {
      method: 'PUT',
    })
      .then(fetch.success)
      .catch(response => {
        throw new Errors.PauseError({
          flowId,
          response,
        });
      });
  },

  unpause(flowId: ID, origin: UnpauseOriginType) {
    return fetch(fetch.url(`${z.cfg.api}/nodes/${flowId}/unpause`), {
      method: 'PUT',
      body: JSON.stringify({
        origin,
      }),
    })
      .then(fetch.success)
      .catch(response => {
        throw new Errors.UnpauseError({
          flowId,
          response,
        });
      });
  },

  refreshNode(
    flowId: ID,
    nodeId: ID,
    attrs: {},
    {
      oldTaskState,
      shouldRunNode,
      source,
      isTemplate,
    }: {
      oldTaskState: FlowTaskState,
      shouldRunNode: boolean,
      source?: string,
      isTemplate: boolean,
    } = {}
  ): Promise<{
    node: ?FlowNode,
    taskStates: ?{ state: FlowTaskState, hasCachedSample: boolean },
    needs: ?Need,
  }> {
    const templateParams = (isTemplate && ZT_API_PARAM) || {};
    const nodeParams = {
      source: source || 'persisted',
      fallback: 'disabled',
      ...templateParams,
    };

    const runNode = shouldRunNode
      ? () => Data.runNode(nodeId, oldTaskState, nodeParams)
      : () => Promise.resolve();

    // This need to run in order, in particular patch needs to run before needs. So don't use Promise.all.
    return Data.patchNode(nodeId, attrs, { isTemplate }).then(node => {
      return runNode().then(taskStates => {
        return Data.needs(nodeId, templateParams).then(needs => {
          return { node, taskStates, needs };
        });
      });
    });
  },

  createNewTrigger(accountId: ID, { isTemplate }: IsTemplate = {}) {
    return postJsonWithAccountId(
      accountId,
      fetch.url(getNodesUrl({ isTemplate })),
      {
        type_of: 'read',
      }
    )
      .then(data => data.id)
      .catch(response => {
        throw new Errors.DataPostError({
          resource: resourceNames.zapTrigger,
          response,
        });
      });
  },

  createNewAction(accountId: ID, flowId: ID, { isTemplate }: IsTemplate = {}) {
    return postJsonWithAccountId(
      accountId,
      fetch.url(getNodesUrl({ isTemplate })),
      {
        type_of: 'write',
        parent_id: flowId,
        root_id: flowId,
      }
    )
      .then(data => data.id)
      .catch(response => {
        throw new Errors.DataPostError({
          resource: resourceNames.zapAction,
          response,
        });
      });
  },

  createZapFromTemplate(
    accountId: ID,
    {
      sharedZap,
      nodeTemplates: templates,
      query,
      referrer,
      isFromNouns,
    }: {
      sharedZap?: { id: ID, title: string },
      nodeTemplates: Array<*>,
      query: FlowQuery,
      referrer?: string,
      isFromNouns: boolean,
    } = {}
  ): Promise<$FlowFixMe> {
    if (sharedZap) {
      // Check if params all have valid mappings to steps.
      const errors = validateParams(sharedZap);
      if (errors.length > 0) {
        // Let's just report first error.
        const firstError = errors[0];
        return Promise.reject(
          new Errors.SharedZapParamError({
            sharedZap: {
              id: sharedZap.id,
            },
            stepIndex: firstError.stepIndex,
            param: firstError.param,
          })
        );
      }
    }

    // Create a Zap from capabilities - Make a Zap module, ZT preview, partner embeds (Simple Zap Setup)
    if (isFromNouns && templates) {
      return Data.createZapFromCapabilities({
        accountId,
        templates,
        isFromNouns,
      });
    }

    // Create a Zap from a ZT
    templates = templates || nodeTemplates(sharedZap, { query });

    return templates.reduce((prev, nodeTemplate, i) => {
      return prev.then(({ flowId = null, nodes = [] } = {}) => {
        nodeTemplate = { ...nodeTemplate };
        if (flowId) {
          nodeTemplate.parent_id = nodes[i - 1].id;
          nodeTemplate.root_id = flowId;
          // Give params real node ids.
          nodeTemplate.params = replaceParams(
            nodeTemplate.params,
            (id, key) => {
              if (!id) {
                // Let's just assume that the param was referencing the trigger.
                return `${flowId}__${key}`;
              }
              const node = nodes[id];
              if (node) {
                return `{{${nodes[id].id}__${key}}}`;
              }
              // We should not be here.
              return `[[INSERT FIELD FOR ${key}]]`;
            },
            {
              isFilter: nodeTemplate.type_of === 'filter',
            }
          );

          if (nodeTemplate.type_of === 'filter') {
            if (_.isArray(nodeTemplate.params.filter_criteria)) {
              nodeTemplate.params.filter_criteria = RuleUtils.fixRuleIds(
                nodeTemplate.params.filter_criteria
              );
            }
          }
        } else {
          const customTemplate = sharedZap && convertDeprecatedParams(query);

          nodeTemplate.meta = {
            ...nodeTemplate.meta,
            referrer,
          };
          if (customTemplate) {
            nodeTemplate.meta.custom_template = customTemplate;
          }
          if (sharedZap) {
            nodeTemplate.guided_recipe_origin_id = sharedZap.id;
            nodeTemplate.title = sharedZap.title;
          }
        }
        return Data.createNode(accountId, nodeTemplate).then(node => {
          return {
            flowId: flowId || node.id,
            nodes: nodes.concat(node),
          };
        });
      });
    }, Promise.resolve());
  },

  async createDefaultZap(
    accountId: ID,
    { isTemplate }: { isTemplate: boolean } = {}
  ) {
    const { nodes } = await Data.bulkSetupZap(
      accountId,
      [{ type_of: 'read' }, { type_of: 'write' }],
      { isTemplate }
    );
    const [trigger] = nodes;
    return { flowId: trigger.id, nodes };
  },

  async createZap(
    accountId: ID,
    {
      sharedZap,
      nodeTemplates: templates,
      folderId,
      query,
      referrer,
      isTemplate,
      isFromNouns,
    }: {
      sharedZap?: { id: ID, title: string },
      nodeTemplates: any,
      folderId: ID,
      query: FlowQuery,
      referrer?: string,
      isTemplate: boolean,
      isFromNouns: boolean,
    } = {}
  ): Promise<{}> {
    const shouldCreateZapFromTemplate = sharedZap || templates;
    const result = shouldCreateZapFromTemplate
      ? await Data.createZapFromTemplate(accountId, {
          sharedZap,
          nodeTemplates: templates,
          query,
          referrer,
          isFromNouns,
        })
      : await Data.createDefaultZap(accountId, { isTemplate });

    const { flowId } = result;
    if (folderId) {
      await Data.moveZap(flowId, folderId);
    }
    return { ...result, isBlank: !shouldCreateZapFromTemplate };
  },

  createNewZapJson({ sharedZapId }: { sharedZapId: ID }): Promise<?mixed> {
    if (sharedZapId) {
      return fetchJson(`${z.cfg.api}/recipes/guided/${sharedZapId}`).catch(
        response => {
          throw new Errors.DataPostError({
            resource: resourceNames.zap,
            response,
          });
        }
      );
    }

    return Promise.resolve(undefined);
  },

  recentIncidents() {
    return fetchJson(
      `${z.cfg.prometheusapi}/incidents/?resolved=false&hours=false`
    ).catch(() => {
      // swallow Prometheus errors as it's non-critical
      // (and we don't want the editor to break if it's down)
      return EMPTY_RECENT_INCIDENTS;
    });
  },

  getPublishForm(nodeId: ID) {
    return fetchJson(`${z.cfg.api}/template-nodes/${nodeId}/publish`).catch(
      response => {
        throw new Errors.DataGetError({
          resource: resourceNames.zapTemplates,
          response,
        });
      }
    );
  },

  updatePublishForm(nodeId: ID, values: {}) {
    return postJson(
      `${z.cfg.api}/template-nodes/${nodeId}/publish`,
      values
    ).catch(response => {
      throw new Errors.DataPostError({
        resource: resourceNames.zapTemplates,
        response,
      });
    });
  },

  async createPath(
    accountId: ID,
    flowId: ID,
    pathsGroupNodeId: ID,
    pathNodeAttrs: CreateNodeParams
  ) {
    const pathNode = await Data.createNode(accountId, {
      ...pathNodeAttrs,
      root_id: flowId,
      parent_id: pathsGroupNodeId,
    });
    const [needs, actionNode] = await Promise.all([
      Data.needs(pathNode.id),
      Data.createNode(accountId, {
        type_of: 'write',
        root_id: flowId,
        parent_id: pathNode.id,
      }),
    ]);

    return { pathNode, needs, actionNode };
  },

  async createPathAndCheckPaused(
    accountId: ID,
    flowId: ID,
    pathsGroupNodeId: ID,
    pathNodeAttrs: CreateNodeParams
  ) {
    const createPathResults = await Data.createPath(
      accountId,
      flowId,
      pathsGroupNodeId,
      pathNodeAttrs
    );
    const rootNode = await Data.node(flowId);
    return { ...createPathResults, paused: rootNode && rootNode.paused };
  },

  async createInitialPathsForGroup(
    accountId: ID,
    flowId: ID,
    parentNodeId: ID,
    pathNodeAttrsList: CreateNodeParams[]
  ) {
    // Execute synchronously, otherwise paths can get get added out of
    // order, resulting in zaps with 'Path B, Path A', which confuses users.
    // In practice we're only creating two paths, so this shouldn't be too bad.
    const results = [];
    for (const attrs of pathNodeAttrsList) {
      const createPathResult = await Data.createPath(
        accountId,
        flowId,
        parentNodeId,
        attrs
      );
      results.push(createPathResult);
    }
    return results;
  },

  async createInitialPathsForGroupAndCheckPaused(
    accountId: ID,
    flowId: ID,
    parentNodeId: ID,
    pathNodeAttrsList: CreateNodeParams[]
  ) {
    const createPathResults = await Data.createInitialPathsForGroup(
      accountId,
      flowId,
      parentNodeId,
      pathNodeAttrsList
    );
    const rootNode = await Data.node(flowId);
    return { createPathResults, paused: rootNode && rootNode.paused, rootNode };
  },

  async createPathsGroup(
    accountId: ID,
    flowId: ID,
    parentNodeId: ID,
    pathNodeAttrsList: CreateNodeParams[]
  ) {
    const params = {
      ...PATHS_GROUP_ATTRS,
      root_id: flowId,
      parent_id: parentNodeId,
    };
    const pathsGroupNode = await Data.createNode(accountId, params);
    const createPathResults = await Data.createInitialPathsForGroup(
      accountId,
      flowId,
      pathsGroupNode.id,
      pathNodeAttrsList
    );
    const rootNode = await Data.node(flowId);
    const paused = rootNode && rootNode.paused;
    return { flowId, pathsGroupNode, createPathResults, paused, rootNode };
  },

  async getCapabilities(accountId: ID, templates: Array<*>) {
    const body = { nodes: templates };

    return postJsonWithAccountId(
      accountId,
      `${z.cfg.api}/nodes/capabilities`,
      body
    );
  },

  async getCapabilitiesBySharedZapId(accountId: ID, sharedZapId: number) {
    return postJsonWithAccountId(
      accountId,
      `${z.cfg.api}/nodes/capabilities/zap-template/${sharedZapId}`
    );
  },

  async bulkSetupZap(
    accountId: ID,
    templates: Array<*>,
    {
      isFromNouns,
      isTemplate,
    }: { isFromNouns?: boolean, isTemplate?: boolean } = {
      isFromNouns: false,
      isTemplate: false,
    }
  ) {
    isFromNouns = isFromNouns || false; // Because our default above will only be used if the whole options obj is undefined
    isTemplate = isTemplate || false;

    const body = { _from_nouns: isFromNouns, nodes: templates };

    return postJsonWithAccountId(
      accountId,
      addTruthyParamsToUrl(`${z.cfg.api}/nodes/bulksetup`, {
        is_template: isTemplate,
      }),
      body
    ).catch(response => {
      throw new Errors.DataPostError({
        resource: resourceNames.zaps,
        response,
      });
    });
  },

  async createZapFromCapabilities({
    accountId,
    templates: fatTemplates,
    isFromNouns,
  }: {
    accountId: ID,
    templates: Array<*>,
    isFromNouns: boolean,
  }) {
    const templates = fatTemplates.map(template =>
      _.pick(template, ['selected_api', 'type_of', 'action', 'title'])
    );

    const result = await Data.bulkSetupZap(accountId, templates, {
      isFromNouns,
    });

    const flowId = _.first(result.nodes).id;

    return { ...result, flowId };
  },
};

export default Data;
